// Smart-default for the dual-tier timeline's active-window zoom + focus.
// Chooses the smallest predefined window step that frames the milestones the
// user still needs to act on, and centres the focus on that span. Returns
// null when no override is appropriate (e.g. every milestone is paid, or
// there are no milestones to frame), in which case the caller falls back
// to the existing today-centred default.
//
// The default is gated on the URL having no explicit `window` / `focus`
// param, so once the user touches the zoom controls or scrubs the focus
// their explicit choice persists.

import type { Milestone } from "../../../../types";
import { deriveMilestoneLifecycleStatus } from "../../../../utils/deriveMilestoneStatus";
import {
  TIMELINE_WINDOW_STEPS,
  type TimelineWindowDays,
} from "./milestoneUrlState";
import { effectiveTimelineDate } from "./timelineGeometry";

export interface SmartTimelineDefault {
  window: TimelineWindowDays;
  focus: string;
}

const MS_PER_DAY = 86_400_000;

function isoDayUTC(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function fromUTCms(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

function clampIso(iso: string, lo: string, hi: string): string {
  if (iso < lo) return lo;
  if (iso > hi) return hi;
  return iso;
}

/** Actionable = anything except `paid`. Paid milestones are terminal —
 *  showing them in the smart-default view crowds the dot density without
 *  giving the user something to act on. The user can always widen the
 *  window manually to see history. */
function isActionable(m: Milestone): boolean {
  return deriveMilestoneLifecycleStatus(m) !== "paid";
}

/**
 * Compute the smart-default timeline window + focus, or `null` when the
 * caller should fall back to the existing today-centred default.
 *
 * Rules:
 *   - Pick all actionable (non-paid) milestones whose effective date sits
 *     inside the contract bounds.
 *   - If none, return null.
 *   - Compute the date range they span (effective date = latest committed
 *     lifecycle date, falling back to plannedDate).
 *   - Pick the smallest `TIMELINE_WINDOW_STEPS` value ≥ that range. When
 *     the range exceeds every step, use the largest step.
 *   - Centre the focus on the midpoint of the range, clamped to the
 *     contract bounds so the resulting window stays inside the contract.
 */
export function smartTimelineDefault(
  milestones: readonly Milestone[],
  contractStart: string,
  contractEnd: string,
): SmartTimelineDefault | null {
  const startMs = isoDayUTC(contractStart);
  const endMs = isoDayUTC(contractEnd);
  if (startMs > endMs) return null;

  const dates: number[] = [];
  for (const m of milestones) {
    if (!isActionable(m)) continue;
    const iso = effectiveTimelineDate(m);
    if (!iso) continue;
    const ms = isoDayUTC(iso);
    if (ms < startMs || ms > endMs) continue;
    dates.push(ms);
  }
  if (dates.length === 0) return null;

  const minMs = Math.min(...dates);
  const maxMs = Math.max(...dates);
  const rangeDays = Math.max(0, Math.round((maxMs - minMs) / MS_PER_DAY));

  // Smallest step ≥ rangeDays. Steps are kept sorted ascending in
  // milestoneUrlState.ts so a linear scan is sufficient and cheap.
  const steps = TIMELINE_WINDOW_STEPS as ReadonlyArray<TimelineWindowDays>;
  const window: TimelineWindowDays =
    steps.find((s) => s >= rangeDays) ?? steps[steps.length - 1];

  const midMs = Math.round((minMs + maxMs) / 2);
  const focus = clampIso(fromUTCms(midMs), contractStart, contractEnd);
  return { window, focus };
}
